import { useState } from "react";
import { Award, Clock, Flame, History, Package } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./homeTheme";
import {
  ACTIVE_AGENT_EARNINGS_SUMMARY_MOCK,
  AIF_INCOME_HISTORY,
} from "./aifIncomeMock";
import { AGENT_INCOME_HISTORY } from "@/lib/mockData";
import { FORT_AGENT_STATUS, MyFortAgentItem } from "@/api/types";
import {
  formatEtherWithDecimals,
  formatThousands,
  formatUsdPrefix,
} from "@/lib/utils";
import { getAgentTierKey, getTokenName } from "@/config/common";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  AGENT_IMAGE_URL,
  calcAgentProgressPct,
  calcAifProgressPct,
} from "@/components/home/MyAgentsSection/constants";
import { CCC_AIF_ADDRESS, USDT_ADDRESS } from "@/config/env";

export function MyAgentActiveDetailDialog({
  agent,
  open,
  onClose,
}: {
  agent: MyFortAgentItem | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [incomeTab, setIncomeTab] = useState<"usdt" | "aif">("usdt");

  if (!agent || agent.status !== FORT_AGENT_STATUS.ACTIVATED) return null;

  const tierLabel = t(getAgentTierKey(agent.name));
  const tagColor = C.amber;
  const activatedDateStr = dayjs(agent.activatedAt).format("YYYY-MM-DD");
  const progressPct = calcAgentProgressPct(agent);
  const aifProgressPct = calcAifProgressPct(agent);

  const { todayEarnedU, dailyRate } = ACTIVE_AGENT_EARNINGS_SUMMARY_MOCK;

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) onClose();
      }}
    >
      <DialogContent
        className="max-w-sm max-h-[85vh] overflow-y-auto mx-auto"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{
                background: `${C.primary}10`,
                border: `1px solid ${C.primary}20`,
              }}
            >
              <img
                src={AGENT_IMAGE_URL}
                alt="AI"
                className="w-6 h-6 object-contain"
              />
            </div>
            <div className="min-w-0 text-left">
              <div className="flex items-center gap-2 flex-wrap">
                <span style={{ color: C.primary }}>{agent.name}</span>
                <span
                  className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                  style={{
                    background: `${tagColor}12`,
                    color: tagColor,
                    border: `1px solid ${tagColor}20`,
                  }}
                >
                  {tierLabel}
                </span>
              </div>
              <div className="text-[10px]" style={{ color: C.dim }}>
                {t("homePage.myAgentCard.activatedAt", {
                  date: activatedDateStr,
                })}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold"
              style={{ background: `${C.green}10`, color: C.green }}
            >
              <div
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: C.green }}
              />
              {t("homePage.myAgentCard.statusRunning")}
            </span>
            <span className="text-[10px]" style={{ color: C.dim }}>
              {t("homePage.myAgentInactiveDetailDialog.giftLevelShort", {
                level: agent.giftGiveLevel,
              })}
            </span>
          </div>

          <div className="grid grid-cols-4 gap-1.5">
            <div
              className="text-center py-2 rounded-lg"
              style={{
                background: `${C.primary}06`,
                border: `1px solid ${C.primary}10`,
              }}
            >
              <div className="text-[9px]" style={{ color: C.dim }}>
                {t("homePage.benefit.custodyBoost")}
              </div>
              <div
                className="text-xs font-bold mono-num truncate px-0.5"
                style={{ color: C.primary }}
              >
                {`${agent.aiManageAddSpeedBp / 100}%`}
              </div>
            </div>
            <div
              className="text-center py-2 rounded-lg"
              style={{
                background: `${C.green}06`,
                border: `1px solid ${C.green}10`,
              }}
            >
              <div className="text-[9px]" style={{ color: C.dim }}>
                {t("homePage.benefit.releaseMultiple")}
              </div>
              <div
                className="text-xs font-bold mono-num truncate px-0.5"
                style={{ color: C.green }}
              >
                {`${agent.exitMultiple / 100}x`}
              </div>
            </div>
            <div
              className="text-center py-2 rounded-lg"
              style={{
                background: `${C.amber}06`,
                border: `1px solid ${C.amber}10`,
              }}
            >
              <div className="text-[9px]" style={{ color: C.dim }}>
                {t("homePage.benefit.aifToken", {
                  tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                })}
              </div>
              <div
                className="text-xs font-bold mono-num truncate px-0.5"
                style={{ color: C.amber }}
              >
                {formatUsdPrefix(
                  formatThousands(
                    formatEtherWithDecimals(agent.aifValueUsdt, 0),
                    0
                  )
                )}
              </div>
            </div>
            {agent.giftGiveLevel > 0 ? (
              <div
                className="text-center py-2 rounded-lg"
                style={{
                  background: `${C.cyan}06`,
                  border: `1px solid ${C.cyan}10`,
                }}
              >
                <div className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.benefit.teamLevel")}
                </div>
                <div
                  className="text-xs font-bold mono-num truncate px-0.5"
                  style={{ color: C.cyan }}
                >
                  {`C${agent.giftGiveLevel}`}
                </div>
              </div>
            ) : null}
          </div>

          <div
            className="rounded-lg p-3"
            style={{
              background: "rgba(34,197,94,0.04)",
              border: `1px solid ${C.primary}10`,
            }}
          >
            <div className="flex justify-between items-end mb-2">
              <div>
                <span className="text-[10px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentActiveDetailDialog.todayEarned")}
                </span>
                <div
                  className="text-xl font-black mono-num"
                  style={{ color: C.green }}
                >
                  +{todayEarnedU} U
                </div>
              </div>
              <div className="text-right">
                <span className="text-[10px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentActiveDetailDialog.dailyRate")}
                </span>
                <div className="text-lg font-bold mono-num">{dailyRate}</div>
              </div>
            </div>
            <div
              className="grid grid-cols-2 gap-3 pt-2"
              style={{ borderTop: `1px solid ${C.border}` }}
            >
              <div>
                <span className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentActiveDetailDialog.cumulativeIncome")}
                </span>
                <div
                  className="text-sm font-bold mono-num"
                  style={{ color: C.primary }}
                >
                  {formatThousands(
                    formatEtherWithDecimals(agent.staticPaidUsdt)
                  )}{" "}
                  U
                </div>
              </div>
              <div className="text-right">
                <span className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentInactiveDetailDialog.exitCap")}
                </span>
                <div className="text-sm font-bold mono-num">
                  {formatThousands(
                    formatEtherWithDecimals(agent.staticCapUsdt)
                  )}{" "}
                  U
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between text-[10px] mb-1">
              <span style={{ color: C.muted }}>
                {t("homePage.myAgentActiveDetailDialog.earningsProgress")}
              </span>
              <span className="mono-num font-bold" style={{ color: C.primary }}>
                {progressPct.toFixed(1)}%
              </span>
            </div>
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progressPct}%`,
                  background: `linear-gradient(90deg, ${C.primary}, #34d399)`,
                }}
              />
            </div>
            <div
              className="flex justify-between text-[9px] mt-1"
              style={{ color: C.dim }}
            >
              <span className="mono-num">
                {formatThousands(formatEtherWithDecimals(agent.staticPaidUsdt))}{" "}
                U
              </span>
              <span className="mono-num">
                {formatThousands(formatEtherWithDecimals(agent.staticCapUsdt))}{" "}
                U
              </span>
            </div>
          </div>

          <div
            className="rounded-lg p-3"
            style={{
              background: "rgba(245,158,11,0.04)",
              border: `1px solid ${C.amber}10`,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Flame size={12} style={{ color: C.amber }} />
              <span className="font-bold text-[11px]">
                {t("homePage.myAgentInactiveDetailDialog.aifSectionTitle", {
                  tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                })}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div>
                <span className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentInactiveDetailDialog.aifTotalLabel")}
                </span>
                <div
                  className="text-xs font-bold mono-num"
                  style={{ color: C.amber }}
                >
                  {formatThousands(
                    formatEtherWithDecimals(agent.aifValueUsdt, 0),
                    0
                  )}{" "}
                  U
                </div>
              </div>
              <div>
                <span className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentInactiveDetailDialog.aifReleasedLabel")}
                </span>
                <div className="text-xs font-bold mono-num">
                  {t("homePage.myAgentCard.aifDays", {
                    paid: agent.aifPaidDays,
                    total: agent.aifDistributeDays,
                  })}
                </div>
              </div>
              <div>
                <span className="text-[9px]" style={{ color: C.dim }}>
                  {t("homePage.myAgentInactiveDetailDialog.aifClaimedLabel")}
                </span>
                <div
                  className="text-xs font-bold mono-num"
                  style={{ color: C.primary }}
                >
                  ≈
                  {formatThousands(
                    formatEtherWithDecimals(agent.aifPaidValueUsdt, 0),
                    0
                  )}{" "}
                  U
                </div>
              </div>
            </div>
            <div
              className="h-1.5 rounded-full overflow-hidden"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, aifProgressPct)}%`,
                  background: `linear-gradient(90deg, ${C.amber}, #fbbf24)`,
                }}
              />
            </div>
          </div>

          <div
            className="rounded-lg overflow-hidden"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <History size={12} style={{ color: C.muted }} />
                  <span className="font-bold text-[11px]">
                    {t("homePage.myAgentActiveDetailDialog.incomeDetailTitle")}
                  </span>
                </div>
                <div
                  className="flex rounded-lg overflow-hidden"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <button
                    type="button"
                    className="px-2.5 py-0.5 text-[9px] font-semibold transition-all"
                    style={
                      incomeTab === "usdt"
                        ? { background: `${C.primary}15`, color: C.primary }
                        : { color: C.dim }
                    }
                    onClick={() => setIncomeTab("usdt")}
                  >
                    {getTokenName(USDT_ADDRESS)}
                  </button>
                  <button
                    type="button"
                    className="px-2.5 py-0.5 text-[9px] font-semibold transition-all"
                    style={
                      incomeTab === "aif"
                        ? { background: `${C.amber}15`, color: C.amber }
                        : { color: C.dim }
                    }
                    onClick={() => setIncomeTab("aif")}
                  >
                    {getTokenName(CCC_AIF_ADDRESS)}
                  </button>
                </div>
              </div>
              {incomeTab === "usdt" ? (
                <div className="space-y-0">
                  {AGENT_INCOME_HISTORY.slice(0, 5).map((h, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center py-2"
                      style={{
                        borderBottom: i < 4 ? `1px solid ${C.border}` : "none",
                      }}
                    >
                      <div>
                        <div className="text-[11px] font-semibold mono-num">
                          +{h.amount} U
                        </div>
                        <div className="text-[9px]" style={{ color: C.dim }}>
                          {h.date}
                        </div>
                      </div>
                      <div className="text-right">
                        <div
                          className="text-[10px] mono-num"
                          style={{ color: C.primary }}
                        >
                          {t("homePage.incomeHistoryDialog.staticIncome", {
                            rate: h.rate,
                          })}
                        </div>
                        <div className="text-[9px]" style={{ color: C.dim }}>
                          {t(
                            "homePage.myAgentActiveDetailDialog.regionVolume",
                            {
                              volume: h.zoneVolume,
                            }
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-0">
                  {AIF_INCOME_HISTORY.slice(0, 5).map((h, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center py-2"
                      style={{
                        borderBottom: i < 4 ? `1px solid ${C.border}` : "none",
                      }}
                    >
                      <div>
                        <div
                          className="text-[11px] font-semibold mono-num"
                          style={{ color: C.amber }}
                        >
                          +{h.aifAmount.toFixed(2)}{" "}
                          {getTokenName(CCC_AIF_ADDRESS)}
                        </div>
                        <div className="text-[9px]" style={{ color: C.dim }}>
                          {h.date}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] mono-num">
                          ≈{h.usdValue} U
                        </div>
                        <div className="text-[9px]" style={{ color: C.dim }}>
                          {t("homePage.incomeHistoryDialog.aifDateDetail", {
                            date: h.date,
                            usdValue: h.usdValue,
                            price: String(h.aifPrice),
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {agent.giftGiveLevel > 0 ? (
            <div
              className="rounded-lg p-3"
              style={{
                background: "rgba(168,85,247,0.04)",
                border: "1px solid rgba(168,85,247,0.10)",
              }}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Award size={12} style={{ color: "#a855f7" }} />
                <span className="font-bold text-[11px]">
                  {t("homePage.myAgentActiveDetailDialog.teamGiftTitle")}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div
                  className="rounded-lg p-2"
                  style={{ background: "rgba(168,85,247,0.04)" }}
                >
                  <span className="text-[9px]" style={{ color: C.dim }}>
                    {t("homePage.myAgentActiveDetailDialog.giftLevelLabel")}
                  </span>
                  <div
                    className="text-sm font-bold mono-num"
                    style={{ color: "#a855f7" }}
                  >
                    C{agent.giftGiveLevel}
                  </div>
                  {agent.giftGiveLevelExpireAt > 0 && (
                    <div className="text-[9px]" style={{ color: C.dim }}>
                      {t("homePage.myAgentActiveDetailDialog.giftValidUntil", {
                        date: dayjs(agent.giftGiveLevelExpireAt).format(
                          "YYYY-MM-DD"
                        ),
                      })}
                    </div>
                  )}
                </div>
                <div
                  className="rounded-lg p-2"
                  style={{ background: "rgba(34,197,94,0.04)" }}
                >
                  <span className="text-[9px]" style={{ color: C.dim }}>
                    {t("homePage.myAgentActiveDetailDialog.custodyBoostLabel")}
                  </span>
                  <div
                    className="text-sm font-bold mono-num"
                    style={{ color: C.primary }}
                  >
                    {agent.aiManageAddSpeedBp / 100}%
                  </div>
                  <div className="text-[9px]" style={{ color: C.dim }}>
                    {t("homePage.myAgentActiveDetailDialog.custodyBoostHint")}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div
            className="rounded-lg p-3"
            style={{ background: C.card2, border: `1px solid ${C.border}` }}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Package size={12} style={{ color: C.cyan }} />
              <span className="font-bold text-[11px]">
                {t("homePage.myAgentInactiveDetailDialog.hardwareTitle")}
              </span>
            </div>
            <div className="flex justify-between text-[11px]">
              <span style={{ color: C.dim }}>
                {t("homePage.myAgentCard.deliveryStatus")}
              </span>
              <span className="font-semibold" style={{ color: C.amber }}>
                {t("homePage.myAgentInactiveDetailDialog.hardwarePending")}
              </span>
            </div>
          </div>

          <div className="text-[10px] space-y-1 pt-1" style={{ color: C.dim }}>
            <div className="flex items-center gap-1">
              <Package size={9} />
              {t("homePage.agentDetailDialog.hardwareNote")}
            </div>
            <div className="flex items-center gap-1">
              <Clock size={9} />
              {t("homePage.myAgentInactiveDetailDialog.aifReleaseFootnote", {
                days: agent.aifDistributeDays,
                tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
